export const sampleContent = [
  {
    id: 1,
    title: `EVs start on fire often`,
    titleBadge: 'False',
    punch: "25 fires per 100,000 EVs sold. Gas cars: about 1,530. Hybrids: about 3,475.",
    barsCaption: "Fires per 100,000 sold",
    bars: [{"label": "EVs", "value": "25", "pct": 1, "tone": "good"}, {"label": "Gas cars", "value": "1,530", "pct": 44, "tone": ""}, {"label": "Hybrids", "value": "3,475", "pct": 100, "tone": "bad"}],
    truth: [
      `Electric vehicles start on fire far less than traditional gasoline-powered vehicles. While it's true that EVs can start on fire, only 25 fires are recorded for every 100,000 EVs sold. In comparison, approximately 1,530 gas vehicles and 3,475 hybrid vehicles were involved in fires on that same 100,000-sold scale.`,
    ],
    truthSnippet: `Electric vehicles start on fire far less than traditional gasoline-powered vehicles. While it's true that EVs can start on fire, only 25 fires are recorded for every 100,000 EVs...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['EV fires'],
    sources: [
      {
        name: 'Fairfax County',
        url: 'https://www.fairfaxcounty.gov/environment-energy-coordination/climate-matters/EV-less-fire-risk'
      }
    ],
  },
  {
    id: 2,
    title: `EVs are too expensive`,
    titleBadge: 'False',
    punch: "Long-range EVs now cost less than the average new gas car, and less to own over five years when financed.",
    truth: [
      `While it's true that purchasing any new vehicle is going to be a costly endeavor, long-range electric vehicles actually cost less than the average internal-combustion engine vehicle. The five-year total cost of ownership of a financed EV is significantly less expensive than an ICE vehicle, and the same can be said for three-year leasing.`,
    ],
    truthSnippet: `While it's true that purchasing any new vehicle is going to be a costly endeavor, long-range electric vehicles actually cost less than the average internal-combustion engine...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Cost'],
    sources: [
      {
        name: 'Bloomberg',
        url: 'https://www.bloomberg.com/news/articles/2024-06-07/long-range-evs-now-cost-less-than-the-average-us-new-car'
      },
      {
        name: 'Auto News',
        url: 'https://www.autonews.com/ev-economics'
      }
    ],
  },
  {
    id: 3,
    title: `EV batteries don't last long`,
    titleBadge: 'False',
    punch: "Plenty of older Teslas have hit 200,000 to 400,000 miles on the original pack, and packs lose only about 2.3% of their capacity a year.",
    truth: [
      `This is one of the most persistent idiotic statements about EVs, and it's flat-out wrong. EV batteries are outlasting the cars themselves. Many older Tesla models have hit 200,000 to 400,000 miles on their original battery packs and are still going strong. On top of that, automakers warranty EV batteries for at least 8 years or 100,000 miles as standard, so even in a worst-case scenario, you're covered.`,
      `Yes, EV batteries do degrade with use, but far slower than people assume, and nothing like the cell phone in your pocket. EV packs lose roughly 2.3% of their capacity per year on average (Geotab's 2026 study of about 22,700 EVs), meaning after 10 years you'd still have about 77% of your original range, above the 70% line most battery warranties guarantee. Most owners never come close to wearing one out before selling the car. The "they're just like phone batteries" comparison is the part that really falls apart: phone batteries get a full charge cycle nearly every day and live in a thin case with almost no thermal management. EV packs have sophisticated liquid cooling and heating, are cycled gently (most owners only use 20-40% of capacity daily), and are engineered for a totally different lifespan. Comparing the two is like comparing a commercial airplane engine to a lawn mower.`,
      `If you want to extend battery life even further, the playbook is simple: avoid relying on DC fast charging for everyday use, don't leave the car baking in extreme heat or freezing for long stretches, and try to keep the state of charge between 20% and 80% when you can. Do that and the pack will likely outlast everything else on the car.`,
    ],
    truthSnippet: `This is one of the most persistent idiotic statements about EVs, and it's flat-out wrong. EV batteries are outlasting the cars themselves. Many older Tesla models have hit 200,000 to 400,000 miles...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Battery life'],
    sources: [
      {
        name: 'CleanTechnica',
        url: 'https://cleantechnica.com/2024/07/14/another-tesla-with-over-400000-miles-on-one-battery/'
      },
      {
        name: 'InsideEVs',
        url: 'https://insideevs.com/news/723734/tesla-model-3y-battery-capacity-degradation-200000miles/'
      },
      {
        name: 'Geotab',
        url: 'https://www.geotab.com/blog/ev-battery-health/'
      },
      {
        name: 'Car and Driver',
        url: 'https://www.caranddriver.com/shopping-advice/a32668797/ev-faqs/'
      }
    ],
  },
  {
    id: 4,
    title: `EVs cause more lifetime emissions than conventional cars`,
    seoTitle: 'EVs cause more lifetime emissions than gas cars: False',
    titleBadge: 'False',
    punch: "On a clean grid an EV makes roughly 15% of a gas car's emissions, and it still comes out ahead on dirty grids.",
    truth: [
      `When electric vehicles first gained traction in the U.S., only a small portion of the country had the right infrastructure for those cars to actually be viable options. This was due in large part to the electrical grid that they would have been charged on being powered by fossil fuels. Today, technologies and infrastructure have improved to the point that on a clean grid, an EV might create roughly 15% of the emissions of a gasoline car. Even on grids powered by fossil fuels today, an EV will produce emissions roughly equal to a gas-powered vehicle that gets 82 mpg, which is still a good deal better than the most efficient gas vehicle (which is the Toyota Prius at 57 mpg).`,
    ],
    truthSnippet: `When electric vehicles first gained traction in the U.S., only a small portion of the country had the right infrastructure for those cars to actually be viable...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Emissions'],
    sources: [
      {
        name: 'CleanTechnica',
        url: 'https://cleantechnica.com/2024/06/02/driving-on-electricity-is-now-much-cleaner-than-using-a-gasoline-car/'
      }
    ],
  },
  {
    id: 5,
    title: `Non-Tesla EVs can access Tesla superchargers`,
    titleBadge: 'True',
    punch: "Tesla opened its Supercharger network to other brands, and many of them already use it.",
    truth: [
      `It is no secret that Tesla has the largest network of charging stations across the country, and as a big name luxury brand, it would be easy to assume they don't play well with others. But in truth, Tesla has always claimed to desire opening its Supercharger network for any and every EV out there, and many brands are already taking advantage. The network includes three different types of chargers, including one type that is exclusive to Tesla. The second type is designed to charge any EV as long as you have a Magic Dock adapter. The third are NACS Superchargers, which can either be accessed through an adapter that many other brands are already handing out or by NACS-equipped vehicles, which are also starting to roll out. These are the brands currently capable of accessing NACS Superchargers: Ford, General Motors, Hyundai, Kia, Lucid, Mercedes-Benz, Nissan, Polestar, Rivian and Volvo.`,
    ],
    truthSnippet: `It is no secret that Tesla has the largest network of charging stations across the country, and as a big name luxury brand, it would be easy to assume they don't...`,
    idiotTypes: [ 'Hungry Idiot'],
    categories: ['Charging'],
    sources: [
      {
        name: 'Tesla',
        url: 'https://www.tesla.com/support/supercharging-other-evs'
      }
    ],
  },
  {
    id: 6,
    title: `EVs are less safe in water`,
    titleBadge: 'False',
    punch: "EVs have no air intakes, so they can't hydrolock, and the battery and cabling are sealed against water.",
    truth: [
      `EVs are actually better equipped to handle water than gas-powered vehicles. The battery pack and all cabling are insulated and shielded to protect against water intrusion, and EVs are put through the same rigorous safety testing as any other vehicle. Here's the kicker: EVs have no air intakes, which means they can't hydrolock -- something that can instantly kill a gas engine when water floods in. No vehicle should be driven through seriously flooded conditions, but if you had to pick one to be in during a flood, the EV is the safer bet.`,
    ],
    truthSnippet: `EVs are actually better equipped to handle water than gas-powered vehicles. The battery pack and all cabling are insulated and shielded to protect against water...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Safety'],
    sources: [
      {
        name: 'Electrifying',
        url: 'https://www.electrifying.com/blog/knowledge-hub/electric-cars-and-floods-everything-you-need-to-know'
      }
    ],
  },
  {
    id: 7,
    title: `EV sales are slowing down`,
    // Oct 2026: verdict flipped from False to True. The old answer cited KBB's
    // Q2 2024 record; since the federal credit ended (Sept 30, 2025) US EV sales
    // have fallen year over year. Figures: Cox Automotive / KBB Q2 2026 EV Sales
    // Report (estimates as of 7/9/2026).
    titleBadge: 'True',
    punch: "The hater has a point: 247,226 sold in Q2 2026, down 20.5% after the federal credit ended.",
    barsCaption: "New EVs sold in the US, Q2",
    bars: [{"label": "2025", "value": "311,064", "pct": 100, "tone": ""}, {"label": "2026", "value": "247,226", "pct": 79.5, "tone": "bad"}],
    truth: [
      `The hater has a point this time. US EV sales fell after the $7,500 federal tax credit ended on September 30, 2025. Cox Automotive's Kelley Blue Book estimates show 247,226 new EVs sold in the second quarter of 2026, down 20.5% from 311,064 a year earlier, and first-half 2026 sales of 462,892, down 23.8%. Losing the credit raised the real price of a new EV overnight, and buyers noticed.`,
      `Two things the headline skips. Sales grew about 15% from the first quarter of 2026 to the second, so the drop is leveling off rather than spiraling. And not every brand is shrinking: Kia, Toyota, Subaru, Rivian and Cadillac all sold more EVs in Q2 2026 than a year earlier. Slower, yes. Over, no. If you are shopping, a slower market usually means more inventory and better deals for you.`,
    ],
    truthSnippet: `The hater has a point this time. US EV sales fell after the federal tax credit ended: 247,226 sold in Q2 2026, down 20.5% from a year earlier...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['EV sales'],
    sources: [
      {
        name: 'Cox Automotive / Kelley Blue Book: Q2 2026 EV Sales Report',
        url: 'https://www.coxautoinc.com/wp-content/uploads/2026/07/Q2-2026-EV-Sales.KBB-Counts.pdf'
      }
    ],
  },
  {
    id: 8,
    title: `Charging an EV takes too long`,
    titleBadge: 'False',
    punch: "Most charging happens at home overnight, and road-trip fast chargers add 200+ miles in 20 to 30 minutes.",
    truth: [
      `Here's what most people miss about EV charging: the vast majority of it happens at home, overnight, while you sleep. You plug in when you get home, wake up with a full battery every single morning -- something gas car owners can never do. No gas station trips, no waiting. For daily driving, charging time is essentially zero. On road trips, DC fast chargers can add 200+ miles of range in 20-30 minutes, which is about the time it takes to use the restroom and grab a coffee. Level 2 home chargers (240-volt, like a dryer outlet) can fully charge most EVs in a few hours. The "charging takes too long" argument only makes sense if you ignore the fact that EV owners spend far less total time refueling than gas car owners do.`,
    ],
    truthSnippet: `Here's what most people miss about EV charging: the vast majority of it happens at home, overnight, while you sleep. You plug in when you get home, wake up with a full battery...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Charging'],
    sources: [
      {
        name: 'Transportation',
        url: 'https://www.transportation.gov/rural/ev/toolkit/ev-basics/charging-speeds'
      }
    ],
  },
  {
    id: 9,
    title: `EV range is worse in cold weather`,
    titleBadge: 'True',
    punch: "It does: up to about 40% at 20°F with the heater running, per the DOE. Gas cars lose 10% to 20%. Warm it up while it's plugged in and a normal day still fits.",
    barsCaption: "Range or mileage lost at 20°F (DOE tests)",
    bars: [{"label": "EV, heater on", "value": "41%", "pct": 100, "tone": "bad"}, {"label": "EV, heater off", "value": "12%", "pct": 29, "tone": ""}, {"label": "Gas car, city", "value": "10-20%", "pct": 49, "tone": ""}],
    truth: [
      `Yes, EV range drops in the cold, and by more than gas mileage does. In the Department of Energy's tests at 20°F, an EV's range fell about 41% with the cabin heater running and about 12% without it. A gas car's city mileage fell 10% to 20% (15% to 33% on short trips). So this one's true. It's still not a dealbreaker: the average American drives about 37 miles a day, and a 300-mile EV that loses 40% still has about 180. Preconditioning (warming the cabin and battery while the car is still plugged in) runs on wall power instead of the battery, heated seats and steering wheels warm you for a fraction of the energy, and the heat pumps in newer EVs hold on to noticeably more range. On a long winter road trip, plan one extra charging stop.`,
    ],
    truthSnippet: `Yes, EV range drops in the cold, and by more than gas mileage does. In DOE tests at 20°F, range fell about 41% with the heater on and 12% with it off...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Range'],
    sources: [
      {
        name: 'Car and Driver',
        url: 'https://www.caranddriver.com/features/a60344222/does-an-ev-work-as-well-in-cold-weather/'
      },
      {
        name: 'EV Engineering Online',
        url: 'https://www.evengineeringonline.com/how-do-cold-weather-and-freezing-temperatures-affect-evs/'
      },
      {
        name: 'FuelEconomy.gov (DOE)',
        url: 'https://www.fueleconomy.gov/feg/coldweather.shtml'
      }
    ],
  },
  {
    id: 11,
    title: `EVs are not American`,
    titleBadge: 'False',
    punch: "No car is truly all-American. Parts come from all over the world, gas or electric.",
    truth: [
      `This is a common complaint about many car companies, but the narrative has recently been applied to electric vehicles in general. The truth is that no car is truly "all-American," as car parts are sourced from countries all over the globe. To put it in perspective, a study by American University highlights the percentage of American parts used by automakers, and both Ford and GM - two of the most widely considered American brands - came in at just 54%. Do you know which brand scored the highest and is, therefore, the most American brand today? Tesla, at 81%. The study also includes a list of models, and the top four spots were claimed by 5 Tesla models, with a tie at #4, and they weren't the only EVs in the top 10.`,
    ],
    truthSnippet: `This is a common complaint about many car companies, but the narrative has recently been applied to electric vehicles...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Politics & Policy'],
    sources: [
      {
        name: 'KBB',
        url: 'https://www.kbb.com/car-news/study-tesla-makes-the-most-american-cars/'
      },
      {
        name: 'American University Kogod Made in America Auto Index',
        url: 'https://kogod.american.edu/autoindex/2024'
      }
    ],
  },
  {
    id: 12,
    title: `I'll freeze to death if I'm stuck in an EV in the cold`,
    seoTitle: "I'll freeze to death stuck in an EV in the cold: False",
    titleBadge: 'False',
    punch: "The heater draws 1.6 to 4.0 kW. Two-thirds of a typical 60 kWh battery keeps you warm for 10 to 25 hours.",
    truth: [
      `This fear is understandable but the math is firmly on the EV's side. Electric vehicles require anywhere from 1.6 to 4.0 kW of power to keep the interior warm. That means a 60-kWh battery with even two-thirds of a full charge can keep you warm for 10 to 25 hours straight. Larger batteries or more charge means even longer. Compare that to a gas car: idling burns about 0.5-1 gallon per hour, so a half-tank might give you 6-10 hours -- and you also risk carbon monoxide poisoning if snow blocks the tailpipe. An EV has no exhaust, so there's zero CO risk. In reality, an EV is one of the safest vehicles to be stranded in during winter.`,
    ],
    truthSnippet: `This fear is understandable but the math is firmly on the EV's side. Electric vehicles require anywhere from 1.6 to 4.0 kW of power to keep the interior warm...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Cold weather'],
    sources: [
      {
        name: 'Recurrent Auto',
        url: 'https://www.recurrentauto.com/research/cold-weather-idling-in-an-ev'
      }
    ],
  },
  {
    id: 13,
    title: `EVs are being forced on me`,
    titleBadge: 'False',
    punch: "No US law has ever required anyone to buy an EV. The rules covered what automakers sell, not what you drive.",
    truth: [
      `There has never been a US law requiring any individual to buy an EV -- every so-called "mandate" regulated what automakers could sell, never what you could buy, own, or drive. And in 2026 this myth is more wrong than it's ever been, because even the automaker-level pressure has mostly been dismantled: the EPA repealed its vehicle greenhouse-gas standards in February 2026 (a repeal now being challenged in court), Congress zeroed out fuel-economy penalties, the federal EV purchase credits are gone, and California's famous 2035 gas-car phase-out lost its federal waiver and is tied up in two lawsuits while most states that adopted it have paused enforcement. Nobody is coming for your gas car -- if anything, current federal policy pushes the other way. People who buy EVs in 2026 are doing it because the car is better for their life: cheaper to run, barely any maintenance, faster, quieter. Anyone still claiming they're "being forced" is arguing with a rule book that got shredded.`,
    ],
    truthSnippet: `There has never been a US law requiring any individual to buy an EV -- every so-called "mandate" regulated what automakers could sell, never what you could buy...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Politics & Policy'],
    sources: [
      {
        name: 'Federal Register',
        url: 'https://www.federalregister.gov/documents/2026/02/18/2026-03157/rescission-of-the-greenhouse-gas-endangerment-finding-and-motor-vehicle-greenhouse-gas-emission'
      },
      {
        name: 'Congress.gov',
        url: 'https://www.congress.gov/bill/119th-congress/house-joint-resolution/88'
      },
      {
        name: 'Harvard EELP',
        url: 'https://eelp.law.harvard.edu/tracker/greenhouse-gas-endangerment-finding/'
      }
    ],
  },
  {
    id: 14,
    title: `There aren't enough EV charging stations`,
    titleBadge: 'False',
    punch: "81,000+ public charging locations and 250,000+ public ports in the US, per the DOE.",
    barsCaption: "Public charging ports in the US",
    bars: [{"label": "Level 2", "value": "177,000", "pct": 100, "tone": "good"}, {"label": "DC fast", "value": "73,000+", "pct": 41, "tone": "good"}],
    truth: [
      `One of the most common complaints regarding the electrification of the automotive industry is the lack of infrastructure throughout the U.S. The truth, per the Department of Energy's live station counts: as of mid-2026 there are more than 81,000 public charging locations in the US with over 250,000 public ports, about 177,000 Level 2 and more than 73,000 DC fast chargers. And the fast-charging buildout is accelerating, not stalling: DC-fast ports grew roughly 33% in 2025 alone. On top of that, Tesla opening its North American Charging Standard connector to other manufacturers means most new non-Tesla EVs can now use the biggest, most reliable network in the country. The "nowhere to charge" complaint is aging about as well as "the internet is a fad."`,
    ],
    truthSnippet: `One of the most common complaints regarding the electrification of the automotive industry is the lack of infrastructure throughout...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Charging'],
    sources: [
      {
        name: 'DOE AFDC',
        url: 'https://afdc.energy.gov/stations/states'
      },
      {
        name: 'EV Charging Stations',
        url: 'https://evchargingstations.com/chargingnews/dc-fast-charging-may-2026/'
      },
      {
        name: 'Consumer Affairs',
        url: 'https://www.consumeraffairs.com/automotive/how-many-ev-charging-stations-are-in-the-us.html'
      },
      {
        name: 'Here',
        url: 'https://www.here.com/learn/blog/us-ev-charging-stations'
      }
    ],
  },
  {
    id: 15,
    title: `Tesla EVs have lots of recalls`,
    titleBadge: 'False',
    punch: "Tesla recalls make the news. Other brands, Ford included, rack up plenty of their own.",
    truth: [
      `It's no secret that Tesla vehicles have received a number of recalls throughout the years, to the point that it seems like they receive more than any other company. But the main reason it seems that way is because of the publicity Tesla receives. When something goes wrong with a Tesla, it's immediately news, whereas an issue with a Ford doesn't become as noteworthy to the press or population. In fact, in 2024 Ford and Chrysler both issued 51 unique recalls, with four other brands issuing more than 20. How many did Tesla issue? 11, which is tied for the fourth fewest alongside Honda. Furthermore, the vast majority of the automaker's recalls have little to do with it being an EV, but rather affect things such as the hood latch, warning lights, TPMS and Autopilot. They also include software updates as recalls, which further inflates the number.`,
    ],
    truthSnippet: `It's no secret that Tesla vehicles have received a number of recalls throughout the years, to the point that it seems like they receive...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Safety'],
    sources: [
      {
        name: 'BRC legal',
        url: 'https://www.brclegal.com/us-car-recall-statistics/'
      },
      {
        name: 'Cars',
        url: 'https://www.cars.com/research/tesla/recalls/?page_size=40'
      },
      {
        name: 'Nymag',
        url: 'https://nymag.com/intelligencer/2023/12/why-is-tesla-recalling-almost-all-its-cars-in-the-u-s.html'
      }
    ],
  },
  {
    id: 16,
    title: `EVs don't help with climate change`,
    titleBadge: 'False',
    punch: "Over a full lifetime, EVs cut emissions, even counting the power plants and the battery.",
    truth: [
      `One of the biggest selling points for the electrification of the industry is that it's better for the environment, ultimately helping with climate change in the long run. There are plenty of detractors out there who think otherwise, but we'll highlight two of their beliefs. The first is that EVs are actually worse for the climate than those with internal combustion engines because of the emissions from the power plants generating the electricity to charge them. While it's true that some sources of power emit carbon pollution, that does not change the fact that an EV creates less pollution than an ICE vehicle on average. The second opinion many detractors bring up is battery manufacturing, but even taking both the manufacturing and charging of EVs into account, they still have a smaller carbon footprint than gas-powered vehicles.`,
    ],
    truthSnippet: `One of the biggest selling points for the electrification of the industry is that it's better for the environment, ultimately helping...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Climate'],
    sources: [
      {
        name: 'EPA Gov',
        url: 'https://www.epa.gov/greenvehicles/electric-vehicle-myths'
      }
    ],
  },
  {
    id: 17,
    title: `EVs are expensive to maintain`,
    titleBadge: 'False',
    punch: "No oil changes and fewer moving parts. AAA's driving-cost data puts EV maintenance per mile below gas.",
    truth: [
      `You've likely heard detractors speak about how expensive it must be to maintain an electric vehicle, but they're only thinking about one part of the vehicle rather than the vehicle as a whole, so let's take a closer look at things. The American Automobile Association, better known as AAA, puts together a study each year called Your Driving Costs, and in this study there is a breakdown of maintenance costs per mile for nine different types of vehicles. When it comes to maintenance costs, electric vehicles are by far the least expensive at 7.89 cents per mile. Medium sedans cost 10.89 cents per mile, while the second least expensive is small sedans at 9.55 cents. If you keep your EV long enough to require a battery replacement, that will indeed be expensive, but overall maintenance is quite a bit less expensive in an EV.`,
    ],
    truthSnippet: `You've likely heard detractors speak about how expensive it must be to maintain an electric vehicle, but they're only thinking about...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Cost'],
    sources: [
      {
        name: 'Cars US News',
        url: 'https://cars.usnews.com/cars-trucks/advice/ev-maintenance-costs'
      },
      {
        name: 'News Room',
        url: 'https://newsroom.aaa.com/wp-content/uploads/2024/08/YDC-Brochure-FINAL-9.2024.pdf'
      }
    ],
  },
  {
    id: 18,
    title: `EV fires are almost impossible to put out`,
    titleBadge: 'False',
    punch: "Fire departments have trained procedures for EV fires, and EVs catch fire far less often to begin with.",
    truth: [
      `While we've already established that the number of EV fires is significantly lower than fires in other types of vehicles, they can still happen. Many people believe when these fires do occur, that they're almost impossible to put out, but that's not the case. The International Association of Fire Chiefs has established and disseminated processes across the nation teaching fire departments and their fighters how to deal with EV fires. While they can certainly take longer to put out than a gas-powered vehicle fire, the big positive is that there isn't as much of a rush to completely extinguish the fire. Fighting an EV fire is more about containment and allowing the battery to burn out completely to prevent the risk of reignition.`,
    ],
    truthSnippet: `While we've already established that the number of EV fires is significantly lower than fires in other types of vehicles, they can...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['EV fires'],
    sources: [
      {
        name: 'IAFC',
        url: 'https://www.iafc.org/topics-and-tools/resources/resource/iafc-s-fire-department-response-to-electric-vehicle-fires-bulletin'
      },
      {
        name: 'Maritime Magazines',
        url: 'https://www.maritimemagazines.com/maritime-reporter/202310/lies-damn-lies-and-inextinguishable-lore-ev-fires/'
      }
    ],
  },
  {
    id: 19,
    title: `EVs are being returned for conventional cars`,
    titleBadge: 'Misleading',
    punch: "A real stat in a trench coat. The trade-in data can't see Tesla owners going back to Tesla.",
    truth: [
      `This one is a real statistic wearing a trench coat. The viral claim -- "51% of Tesla owners switch back to gas" -- comes from Edmunds trade-in data that only counts Teslas traded in at franchise dealerships. Tesla doesn't have franchise dealerships, so that dataset structurally excludes the most common outcome: a Tesla owner going back to Tesla for another one. Registration data that sees the whole market (S&P Global Mobility) tells the real story: roughly 70% of Tesla households that move on from a Tesla buy another Tesla, and a majority of EV households -- about 59% in 2025 -- stay electric. That loyalty number has genuinely softened since the federal credits died (it was closer to 68% in 2023), and yes, some owners do go back to gas. But it's a minority outcome, and owner-intent surveys agree: J.D. Power's 2026 study found 96% of EV owners would consider another EV. "More than half are switching back" isn't a trend -- it's a misread spreadsheet.`,
    ],
    truthSnippet: `This one is a real statistic wearing a trench coat. The viral "51% switch back to gas" claim comes from trade-in data that structurally excludes the most common outcome...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['EV sales'],
    sources: [
      {
        name: 'Inside EVs',
        url: 'https://insideevs.com/news/729112/edmunds-tesla-trade-ins-debunked/'
      },
      {
        name: 'S&P Global Mobility',
        url: 'https://www.spglobal.com/automotive-insights/en/blogs/2025/09/tesla-brand-loyalty-evolving-ev-market'
      },
      {
        name: 'J.D. Power',
        url: 'https://www.jdpower.com/business/press-releases/2026-us-electric-vehicle-experience-evx-ownership-study'
      }
    ],
  },
  {
    id: 20,
    title: `EVs are just run on coal`,
    titleBadge: 'False',
    punch: "Even on the dirtiest grid, an EV beats a comparable gas car. On a fossil-heavy grid it's like an 82 mpg car.",
    truth: [
      `This is one of the fossil fuel industry's favorite talking points, and it falls apart under any scrutiny. Even on the dirtiest electrical grids in the country, an EV produces fewer lifetime emissions than a comparable gas car. On a fossil-fuel-heavy grid, an EV still performs equivalent to a gas car getting 82 mpg -- far better than any gas vehicle on the road (the most efficient is the Toyota Prius at 57 mpg). And here's the key point: only 10% of EVs sold in the US are in states where 50%+ of electricity comes from coal, while over half are sold in states with significant renewable energy. As the grid gets cleaner every year -- and it is, rapidly -- every EV on the road automatically gets cleaner too. A gas car will pollute the exact same amount the day you junk it as the day you bought it.`,
    ],
    truthSnippet: `This is one of the fossil fuel industry's favorite talking points, and it falls apart under any scrutiny. Even on the dirtiest electrical grids in the country, an EV produces fewer...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Emissions'],
    sources: [
      {
        name: 'EV Adoption',
        url: 'https://evadoption.com/are-evs-charged-mostly-by-coal-power-in-the-us/'
      }
    ],
  },
  {
    id: 21,
    title: `I can't travel if the EV infrastructure doesn't exist`,
    titleBadge: 'False',
    punch: "Road trips work. Fast chargers keep spreading along highways, and planning a stop takes a minute.",
    truth: [
      `What about road trips and other long distance traveling? This is a question a lot of people ask when it comes to electric vehicles, and while all-electric ranges have been increasing each year it's still a valid question. Even if your vehicle gets over 300 miles on a full charge, what if you're traveling 600 miles? If you need to travel in your EV, you certainly can, as the infrastructure continues to expand. As long as you plan your route, you'll be able to make appropriate stops at one of the 81,000+ public charging locations (over 250,000 ports) across the country as of mid-2026, and with a Tesla-plug (NACS) adapter, most non-Tesla EVs can now use Tesla's Supercharger network on top of that. All 50 states have public charging.`,
    ],
    truthSnippet: `What about road trips and other long distance traveling? This is a question a lot of people ask when it comes to electric vehicles, and...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Charging'],
    sources: [
      {
        name: 'World Population Review',
        url: 'https://worldpopulationreview.com/state-rankings/ev-charging-stations-by-state'
      }
    ],
  },
  {
    id: 22,
    title: `EVs can't charge on regular outlets`,
    titleBadge: 'False',
    punch: "Almost any EV can charge from a regular outlet with a portable cord. Most include one; Tesla sells it separately. It's slow, but it works.",
    truth: [
      `Although it might not be as practical, this is inherently false as you can technically charge your electric vehicle on any outlet, even your regular AC outlets at home. Most EVs come with a portable Level 1 charging cord (Tesla sells its cord separately), which works with 110-volt outlets - the same outlets found just about anywhere around your home. One thing to keep in mind is that utilizing a 110v outlet to charge your EV is going to take far longer to fully charge; however, most typical commutes drain very little of your battery, meaning you'll only need to charge 10-20% to be full by morning. For those EV buyers that drive their vehicle more, anyone can upgrade their garage with a wall-mounted Level 2 charger. This still isn't going to be as quick as the DC fast charging stations you find in public, but it will cut down your time to charge compared to Level 1 by quite a bit.`,
    ],
    truthSnippet: `Although it might not be as practical, this is inherently false as you can technically charge your electric vehicle on any outlet, even your...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Charging'],
    sources: [
      {
        name: 'Energy Gov',
        url: 'https://www.energy.gov/energysaver/articles/ev-charging-home'
      }
    ],
  },
  {
    id: 23,
    title: `The nation's electrical grid can't handle EVs`,
    titleBadge: 'False',
    punch: "Demand will rise as EVs grow, but gradually, and grid planners are already building for it.",
    truth: [
      `Without a doubt one of the biggest fears spread amongst the general American population in regards to the electrification of the automotive industry is that the nation's electrical grid won't be able to handle it. With nearly 3.5 million EVs on the road at the end of 2023, and a projection of nearly 26.5 million EVs by 2030, the demands for electricity are going to see a marked increase over the next five years. So, can the grid handle it? Using historic information and future projections, the answer is that it shouldn't be a problem at all, as trends dictate we'll see around a 1% increase in required electricity production per year, and the U.S. has seen a 3.2% average increase in production each year for several decades. That's not to say that work can't or shouldn't be done to improve the grid, but it should have no problems handling an influx of EV production over the next several years.`,
    ],
    truthSnippet: `Without a doubt one of the biggest fears spread amongst the general American population in regards to the electrification of the automotive...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Politics & Policy'],
    sources: [
      {
        name: 'Cars US News',
        url: 'https://cars.usnews.com/cars-trucks/advice/can-the-nations-electrical-grid-support-electric-cars'
      }
    ],
  },
  {
    id: 24,
    title: `EV batteries have a problem with road salt`,
    titleBadge: 'False',
    punch: "The battery is shielded underneath. Road salt is almost zero threat; deep saltwater flooding is the real risk.",
    truth: [
      `The vast majority of electric vehicles are built with protecting their batteries in mind. While the battery is typically located under the floor of the vehicle, it has all sorts of external protection to prevent things like road salt and water from touching it. So, on its own, road salt is almost zero threat to EV batteries. However, the real threat comes in the form of saltwater, which can obviously be created from road salt. Saltwater can cause short circuiting in your EV's battery, which can ultimately cause fires, if it spreads between both its positive and negative terminals. But this is only a real issue if your EV is left submerged in saltwater, such as from ocean flooding. So, you won't want to take your EV water fording near the ocean, and make sure to park in high places if winter flooding is a concern.`,
    ],
    truthSnippet: `The vast majority of electric vehicles are built with protecting their batteries in mind. While the battery is typically located under...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Safety'],
    sources: [
      {
        name: 'NHTSA Gov',
        url: 'https://www.nhtsa.gov/sites/nhtsa.gov/files/2024-02/16180-NSR-231214-003_SAE_Teardown%20Analysis%20of%20Flood-damaged%20Evs-tag.pdf'
      },
      {
        name: 'SC Edu',
        url: 'https://sc.edu/uofsc/posts/2024/11/conversation-flooding-fire-threat-ev-lithium-ion-batteries.php'
      }
    ],
  },
  {
    id: 25,
    title: `Taxpayers pay for EV credits`,
    titleBadge: 'True',
    punch: "True, just like oil and gas subsidies, which run $20 billion+ a year. The federal EV credit ended Sept 30, 2025.",
    truth: [
      `Yes, taxpayers fund EV tax credits -- just like they fund subsidies for oil and gas, which receive an estimated $20 billion or more per year in federal subsidies and have for over a century. The federal EV tax credit was up to $7,500 per vehicle, though it was repealed and ended for purchases after September 30, 2025 (state and utility incentives still exist). A 2024 study by economists from multiple universities found that EV credits under the Inflation Reduction Act directly benefit buyers, have measurably slowed climate change effects, and have shifted manufacturing back to U.S. soil -- the credits literally require vehicles to be assembled in North America to qualify. Meanwhile, oil subsidies come with no such requirements and prop up an industry that contributes to air pollution responsible for tens of thousands of premature deaths annually. If you're upset about taxpayer money going to EV credits, you should be furious about fossil fuel subsidies.`,
    ],
    truthSnippet: `Yes, taxpayers fund EV tax credits -- just like they fund subsidies for oil and gas, which receive an estimated $20 billion or more per year in federal subsidies...`,
    idiotTypes: [ 'Angry Idiot'],
    categories: ['Subsidies'],
    sources: [
      {
        name: 'VC research',
        url: 'https://vcresearch.berkeley.edu/news/evaluating-benefits-ev-subsidies-under-inflation-reduction-act'
      }
    ],
  },
  {
    id: 26,
    title: `EV batteries use cobalt, which is mined by children`,
    seoTitle: 'EV batteries use cobalt mined by children: Misleading',
    titleBadge: 'Misleading',
    punch: "Some DRC cobalt mining involves child labor, and that's real. EVs are far from the only buyer.",
    truth: [
      `This one has a kernel of truth wrapped in a lot of misdirection. Yes, cobalt is used in many lithium-ion batteries, and yes, the Democratic Republic of the Congo produces more than 70% of the world's cobalt -- and a portion of that mining (specifically the small "artisanal" sector, which is roughly 15-30% of DRC output) does involve child labor. That part is real and worth being angry about. The misleading part is the framing that EVs are somehow uniquely responsible. Cobalt has been in your cell phone, laptop, tablet, power tools, cordless vacuum, wireless headphones, and medical equipment for decades. If cobalt mining bothers you, the EV is the smallest device in your house using it.`,
      `It also ignores how fast the EV industry is actively moving away from cobalt. Lithium-iron-phosphate (LFP) batteries -- which contain zero cobalt -- now power roughly 40% of new EVs sold globally, including the standard-range Tesla Model 3 and Model Y, most BYD vehicles, and a growing share of Ford and Rivian packs. Sodium-ion batteries (also cobalt-free) are starting to hit the market. The cobalt content of the average EV battery has dropped more than 60% since 2018 as manufacturers shift to high-nickel and cobalt-free chemistries. On top of that, automakers like Tesla, Ford, and GM have signed onto supply-chain auditing programs (the Responsible Minerals Initiative, IRMA, etc.) specifically to keep artisanally-mined DRC cobalt out of their packs.`,
      `Bottom line: the underlying concern about exploitative mining is legitimate, but pinning it on EVs while the same metal sits in the phone you're typing the complaint on is the misleading part.`,
    ],
    truthSnippet: `This one has a kernel of truth wrapped in a lot of misdirection. Yes, cobalt is used in many lithium-ion batteries, and a portion of DRC mining does involve child labor -- but EVs are far from uniquely responsible...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot'],
    categories: ['Child labor'],
    sources: [
      {
        name: 'Dol Gov',
        url: 'https://www.dol.gov/sites/dolgov/files/ILAB/images/storyboards/cobalt/508_How_Batteries_Are_Powered_0922.pdf'
      },
      {
        name: 'News Mit Edu',
        url: 'https://news.mit.edu/2024/cobalt-free-batteries-could-power-future-cars-0118'
      },
      {
        name: 'Drive Electric TN',
        url: 'https://www.driveelectrictn.org/what-are-sodium-ion-batteries-and-how-do-they-compare-to-lithium-ion-batteries'
      },
    ],
  },
  {
    id: 27,
    title: `Hydrogen Fuel Cell Vehicles are more efficient than EVs`,
    seoTitle: 'Hydrogen fuel cell cars are more efficient than EVs: False',
    titleBadge: 'False',
    punch: "EVs are far more efficient than hydrogen cars, which lose energy making, moving and converting hydrogen.",
    truth: [
      `Another type of alternative-fuel vehicle that has been around for quite some time is the Hydrogen Fuel Cell Vehicle. The first HFCV came in 1966, but the first mass-produced model wasn't until 2013. A lot of people think that hydrogen-based vehicles are an even better answer than regular electric vehicles, but in actuality this statement is false. EVs are actually more efficient than their hydrogen counterparts. An infographic from Transport & Environment depicts the difference between them excellently, breaking down where energy losses occur for all three types of vehicles: battery electric, fuel cell and conventional. While conventional cars have an overall efficiency of 13%, HFCVs only increase that figure to 22% whereas EVs offer 73%.`,
    ],
    truthSnippet: `Another type of alternative-fuel vehicle that has been around for quite some time is the Hydrogen Fuel Cell Vehicle. The first HFCV...`,
    idiotTypes: [ 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Emissions'],
    sources: [
      {
        name: 'Inside EVs (Transport & Environment data)',
        url: 'https://insideevs.com/news/332584/efficiency-compared-battery-electric-73-hydrogen-22-ice-13/'
      }
    ],
  },
  {
    id: 28,
    title: `Tesla is going bankrupt`,
    titleBadge: 'False',
    punch: "The Tesla-is-going-bankrupt story runs every year. Tesla is still here.",
    truth: [
      `No matter which type of idiot you are, there's a solid chance you've heard this phrase a handful of times throughout the past several years. That's because it's actually a story that makes the rounds literally every year. Every year multiple news stories surrounding Tesla claim, for some reason or another - whether it be the fact that they were burning through cash, that they planned to sell a vehicle for too cheap, or that they're facing some dangerous issue - that they could, should or would be going bankrupt. But the proof is in the pudding -- and the pudding is the balance sheet. In the third quarter of 2025 alone, Tesla pulled in a record $28 billion in revenue, booked $1.4 billion in profit, and was sitting on tens of billions of dollars in cash. Companies actually circling bankruptcy don't post record quarters with a war chest like that. Tesla has proven its detractors wrong time and again, and they're showing no REAL signs of slowing down anytime soon.`,
    ],
    truthSnippet: `No matter which type of idiot you are, there's a solid chance you've heard this phrase a handful of times throughout the past...`,
    idiotTypes: [ 'Angry Idiot' ,'Curious Idiot', 'Hungry Idiot'],
    categories: ['Tesla'],
    sources: [
      {
        name: 'Tesla Q3 2025 SEC Filing',
        url: 'https://www.sec.gov/Archives/edgar/data/0001318605/000162828025045861/exhibit991.htm'
      }
    ],
  },
  {
    id: 29,
    title: `Rivian owners are more satisfied than any other brand's owners`,
    seoTitle: "Rivian owners are the most satisfied of any brand: True",
    titleBadge: 'True',
    punch: "In J.D. Power's APEAL owner-satisfaction study, Rivian outscores every brand. It just isn't eligible for the trophy.",
    truth: [
      `It's difficult to argue with how satisfied someone is with a product or service. That's how they feel, and no matter what someone else thinks or believes, it's not going to change how someone feels. That's what makes things like J.D. Power surveys and awards such great benchmarks within the automotive industry. For example, the U. S. Automotive Performance, Execution and Layout (APEAL) Study showcases the overall satisfaction automobile owners feel with their vehicle. J.D. Power then compiles those vehicles into a point-based system that compares overall brands against one another. Although brands like Rivian and Tesla are not eligible to be given awards, they still go through the process. While the actual winner of the 2024 APEAL Study was Porsche with 891, Rivian surpassed them with a score of 900. Similarly, Rivian was the only brand to receive a 5/5 Satisfaction Score in Consumer Reports' owner satisfaction surveys.`,
    ],
    truthSnippet: `It's difficult to argue with how satisfied someone is with a product or service. That's how they feel, and no matter what someone...`,
    idiotTypes: [ 'Hungry Idiot'],
    categories: ['Reliability'],
    sources: [
      {
        name: 'JD Power',
        url: 'https://www.jdpower.com/business/press-releases/2024-us-automotive-performance-execution-and-layout-apeal-study'
      },
      {
        name: 'Consumer Reports',
        url: 'https://www.consumerreports.org/cars/car-reliability-owner-satisfaction/most-and-least-liked-car-brands-a1291429338/'
      }
    ],
  },
  {
    id: 30,
    title: `EVs don't have as much range as conventional cars`,
    titleBadge: 'True',
    punch: "Per tank, gas wins. But the average American drives about 37 miles a day, and even cheap EVs do 200+.",
    barsCaption: "Miles",
    bars: [{"label": "Your day", "value": "37", "pct": 18.5, "tone": "bad"}, {"label": "Cheap EVs", "value": "200+", "pct": 100, "tone": "good"}],
    truth: [
      `On paper, most gas cars can go further on a full tank than most EVs can on a full charge -- but that stat misses the point entirely. The average American drives about 37 miles per day. Even the most affordable EVs on the market offer 200+ miles of range, covering nearly a week of typical driving on a single charge. For the 95%+ of driving that's daily commuting and errands, range is a non-issue. And EV ranges keep climbing. Here are the top five highest all-electric ranges on the market in 2025: Lucid Air at 512 miles, Cadillac Escalade IQ at 460 miles, Rivian R1T at 420 miles, Tesla Model S at 402 miles, and Mercedes-Benz EQS 450+ at 390 miles. For road trips, fast chargers can add 200+ miles in 20-30 minutes. Range anxiety is becoming range irrelevance.`,
    ],
    truthSnippet: `On paper, most gas cars can go further on a full tank than most EVs can on a full charge -- but that stat misses the point entirely. The average American drives about 37 miles per day...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Range'],
    sources: [
      {
        name: 'Coltura',
        url: 'https://coltura.org/electric-car-battery-range/'
      }
    ],
  },
  {
    id: 31,
    title: `EVs are eligible for tax credits`,
    titleBadge: 'Misleading',
    punch: "The federal $7,500 credit ended Sept 30, 2025. Your state or utility may still pay.",
    truth: [
      `This used to be one of the biggest reasons to buy an EV -- but the rules changed, so the answer is now "it depends." The federal Clean Vehicle Credit of up to $7,500 for a new EV (and $4,000 for a used one) was repealed and ended for any vehicle purchased after September 30, 2025. So if a salesperson tells you the EV comes with a $7,500 federal credit today, they're working off an old script.`,
      `The good news is that plenty of money is still on the table -- just not from the IRS. Many states offer their own rebates worth a few thousand dollars, and lots of utility companies will pay you $500 to $1,500 just for owning an EV and charging during off-peak hours. (The federal home-charger credit, 30C, ended June 30, 2026 -- though a charger installed by that date can still be claimed on that year's return with IRS Form 8911.) It's a patchwork that changes by ZIP code and by month, so the move is to check exactly what your state, city, and utility offer where you live before you buy.`,
    ],
    truthSnippet: `This used to be one of the biggest reasons to buy an EV -- but the rules changed. The federal $7,500 credit ended September 30, 2025; state and utility incentives still exist...`,
    idiotTypes: [ 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Subsidies'],
    sources: [
      {
        name: 'IRS',
        url: 'https://www.irs.gov/credits-deductions/credits-for-new-clean-vehicles-purchased-in-2023-or-after'
      }
    ],
  },
  {
    id: 33,
    title: `EV batteries end up in landfills`,
    titleBadge: 'False',
    punch: "Tesla says 0% of its batteries end up in landfills; old packs get recycled.",
    truth: [
      `Despite the fact that many people seem to believe EV batteries wind up at landfills, which would admittedly be a very bad thing for the environment, Tesla has confirmed that 0% of its batteries end up in landfills. How is that possible? Because 100% of its old and no-longer-usable batteries are recycled instead! They have even gone so far as to introduce their own in-house battery recycling systems. Tesla isn't alone in this regard, as other EV companies recycle their batteries as well. For example, Nissan recycles its old batteries into street lights in Japan, while Hyundai uses its old recycled batteries to shore up electrical grids.`,
    ],
    truthSnippet: `Despite the fact that many people seem to believe EV batteries wind up at landfills, which would admittedly be a very bad thing for the environment, Tesla has confirmed...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Climate'],
    sources: [
      {
        name: 'Tesla (recycling)',
        url: 'https://www.tesla.com/support/sustainability-recycling'
      },
      {
        name: 'Tesla Impact Report',
        url: 'https://www.tesla.com/impact'
      },
      {
        name: 'Motor Biscuit',
        url: 'https://www.motorbiscuit.com/what-does-tesla-do-with-old-batteries/'
      }
    ],
  },
  {
    id: 34,
    title: `EVs crash more often than conventional cars`,
    titleBadge: 'False',
    punch: "Insurance data shows EV claims run roughly 20% lower than for gas cars.",
    barsCaption: "Insurance claim frequency",
    bars: [{"label": "Gas cars", "value": "Baseline", "pct": 100, "tone": ""}, {"label": "EVs", "value": "~20% less", "pct": 80, "tone": "good"}],
    truth: [
      `There are tons of different reasons a vehicle might crash, many of which have very little to do with what kind of vehicle is actually being driven. Even still, a lot of detractors believe that EVs are out there crashing more often than conventional cars, but the truth is actually quite the opposite. According to a study by the Highway Loss Data Institute, insurance claims for electric vehicles occur roughly 20% less frequently than those for conventional cars, which indicates that they actually crash less often.`,
    ],
    truthSnippet: `There are tons of different reasons a vehicle might crash, many of which have very little to do with what kind of vehicle is actually being driven. Even still, a lot of detractors...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Safety'],
    sources: [
      {
        name: 'Torque News',
        url: 'https://www.torquenews.com/1083/hdli-study-finds-evs-crash-less-often-conventional-cars'
      }
    ],
  },
  {
    id: 35,
    title: `Hydrogen cars are the future, not EVs`,
    titleBadge: 'False',
    punch: "Hydrogen had decades and sold about 70,000 cars worldwide by 2022. Plug-ins passed 26 million.",
    barsCaption: "Sold worldwide by 2022",
    bars: [{"label": "Plug-ins", "value": "26M+", "pct": 100, "tone": "good"}, {"label": "Hydrogen", "value": "~70,200", "pct": 1, "tone": "bad"}],
    truth: [
      `This is one of those claims that sounds reasonable in theory but has been completely disproven by reality. Hydrogen fuel cell vehicles have been around since the 1960s and have had decades to gain traction -- and they haven't. As of 2022, only about 70,200 hydrogen cars had been sold worldwide compared to over 26 million plug-in electric vehicles. In the US in 2023, just 3,143 hydrogen cars were sold versus about 1.2 million battery EVs.`,
      `The infrastructure tells an even bleaker story. The entire United States has only 53 public hydrogen refueling stations, 52 of which are in California, compared to more than 81,000 public EV charging locations nationwide. By 2020, all but three automakers had abandoned plans to manufacture hydrogen passenger cars. Toyota Mirai sales fell to just 1,702 units globally through November 2024. Only three hydrogen cars are on the US market, and only in California: the Toyota Mirai, the Hyundai Nexo and the Honda CR-V e:FCEV, which you can only lease.`,
      `Hydrogen may have a future in heavy-duty trucking, shipping, or industrial use where batteries are impractical, but for passenger vehicles, the market has spoken decisively. EVs won.`,
    ],
    truthSnippet: `This is one of those claims that sounds reasonable in theory but has been completely disproven by reality. Hydrogen fuel cell vehicles have been around since the 1960s and have had decades...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['EV sales'],
    sources: [
      {
        name: 'Wikipedia',
        url: 'https://en.wikipedia.org/wiki/Hydrogen_vehicle'
      },
      {
        name: 'Cars.com: 2026 Toyota Mirai',
        url: 'https://www.cars.com/research/toyota-mirai-2026/'
      }
    ],
  },
  {
    id: 36,
    title: `Replacing an EV battery costs $25,000`,
    titleBadge: 'False',
    punch: "That's a worst case from Tesla's top-end Plaid models. A Standard Range Model 3 pack typically runs $12,000 to $14,000 out of warranty.",
    barsCaption: "Battery replacement cost",
    bars: [{"label": "The claim", "value": "$25,000", "pct": 100, "tone": "bad"}, {"label": "Model 3 SR", "value": "$12-14K", "pct": 56, "tone": "good"}],
    truth: [
      `This number gets thrown around constantly, and it's almost always wrong. EV battery replacement costs vary by pack size and model, but the $25,000 figure is a worst case from Tesla's top-end Plaid models, presented as if it's the norm. Take Tesla as the example since it's the most-cited brand: real-world out-of-warranty replacements on a Standard Range Model 3 typically run $12,000-$14,000, and Reddit owners have posted service receipts as low as $11,000-$13,000 for the full job including labor. Long Range Model 3 and Model Y packs come in around $13,000-$16,000 installed. Even the larger Model S and Model X packs generally land between $13,000 and $20,000 -- not $25,000.`,
      `More importantly, almost no one actually pays out of pocket for a new pack. Automakers warranty EV batteries for at least 8 years or 100,000 miles as standard, and modern packs routinely outlast the rest of the car (see "EV batteries don't last long"). On top of that, third-party refurbishers offer rebuilt packs at a fraction of OEM pricing, most degraded packs can be repaired at the module level rather than swapped wholesale, and industry-wide battery pack prices have dropped roughly 90% since 2010 -- and they're still falling.`,
      `Bottom line: the "$25,000 battery" is a scary sticker price for a repair the vast majority of EV owners will never need -- and when they do, it usually costs far less.`,
    ],
    truthSnippet: `This number gets thrown around constantly, and it's almost always wrong. EV battery replacement costs vary by pack size and model, but the $25,000 figure is a worst case from Tesla's top-end Plaid models...`,
    idiotTypes: [ 'Angry Idiot', 'Curious Idiot', 'Hungry Idiot'],
    categories: ['Cost'],
    sources: [
      {
        name: 'Recurrent Auto',
        url: 'https://www.recurrentauto.com/research/tesla-battery-replacement-costs'
      },
      {
        name: 'SlashGear',
        url: 'https://www.slashgear.com/1962553/tesla-battery-how-much-cost-replace-driver-reports/'
      },
      {
        name: 'Tesla Motors Club',
        url: 'https://teslamotorsclub.com/tmc/threads/firsthand-experience-with-out-of-warranty-battery-replacement-cost.327730/'
      },
      {
        name: 'BloombergNEF',
        url: 'https://about.bnef.com/blog/lithium-ion-battery-pack-prices-hit-record-low-of-139-kwh/'
      }
    ],
  }
];
